<template>
  <div class="w-full">
    <h1 class="text-xl text-center mb-6">
      {{ $t('mycountly.signup.create-an-account') }}
    </h1>
    <cly-request
      disable-loading-icon
      silent-error
      @success="onSuccess"
      @error="onError"
    >
      <template #default="request">
        <cly-form>
          <template #default="v">
            <cly-form-step
              id="signupStep"
              @enter="handleEnter(request, v)"
            >
              <cly-form-field
                :rules="'required|max:72|min:2'"
                class="flex flex-col"
                :label="$t('mycountly.signup.full-name')"
              >
                <el-input
                  id="input__signup-fullName"
                  v-model="signup.fullName"
                  data-test="signup-fullName"
                  class="w-full mt-1"
                  :placeholder="$t('mycountly.signup.placeholder.enter-your-name')"
                />
              </cly-form-field>
              <cly-form-field
                :rules="'required|email|max:320'"
                :label="$t('mycountly.signup.email')"
              >
                <el-input
                  id="input__signup-email"
                  v-model="signup.email"
                  data-test="signup-email"
                  type="email"
                  class="w-full mt-1"
                  :placeholder="$t('mycountly.signup.placeholder.enter-your-email')"
                />
              </cly-form-field>
              <cly-form-field
                :password-validation="true"
                vid="password"
                :rules="'required|min:8|max:32|one_uppercase|one_lowercase|one_number|one_special'"
                :label="$t('mycountly.signup.password')"
              >
                <template #default="validation">
                  <el-input
                    id="input__signup-password"
                    v-model="signup.password"
                    data-test="signup-password"
                    type="password"
                    show-password
                    class="w-full mt-1"
                    placeholder="Create a password"
                    @blur="passwordFocused = false"
                    @focus="passwordFocused = true"
                  />
                  <password-validation
                    v-if="passwordFocused && validation.errors.length"
                    :failed="validation.failedRules"
                  />
                </template>
              </cly-form-field>
              <cly-form-field
                class="!py-1"
              >
                <el-checkbox
                  id="input__signup-terms-and-conditions"
                  v-model="termsAndConditionsChecked"
                  data-test="signup-check-1"
                  class="w-full text-sm !font-normal"
                >
                  {{ $t('mycountly.dashboard.prelogin.i-agree-to-the') }}
                  <a
                    href="https://countly.com/legal/terms-of-service"
                    target="_blank"
                    class="underline"
                  >{{ $t('mycountly.dashboard.prelogin.terms-of-service') }}</a>
                </el-checkbox>
              </cly-form-field>
              <cly-form-field
                class="!py-1"
              >
                <el-checkbox
                  id="input__signup-privacy-policy"
                  v-model="privacyPolicyChecked"
                  data-test="signup-check-2"
                  class="w-full text-sm !font-normal"
                >
                  {{ $t('mycountly.dashboard.prelogin.i-agree-to-the') }}
                  <a
                    href="https://countly.com/legal/privacy-policy"
                    target="_blank"
                    class="underline"
                  >{{ $t('mycountly.dashboard.prelogin.privacy-policy') }}</a>
                </el-checkbox>
              </cly-form-field>
              <myc-big-button
                id="button__signup-submit"
                test-id="signup-submit"
                :disabled="request.pending || !v.isSubmissionAllowed || !areCheckboxesOK"
                type="success"
                class="w-full !mt-5"
                @clicked="handleEnter(request, v)"
              >
                {{ $t('mycountly.signup.create-account') }}
              </myc-big-button>
            </cly-form-step>
          </template>
        </cly-form>
      </template>
    </cly-request>
    <p class="text-center mt-6 text-neutral-500 text-[13px]">
      {{ $t('mycountly.signup.already-have-an-account') }}
      <router-link
        class="!text-[#1EAB56] hover:!text-[#178f47]"
        to="/account/signin"
      >
        Login
      </router-link>
    </p>
  </div>
</template>

<script>
import { mapActions } from 'pinia';
import { useAuthStore } from '@/stores';
import { PreloginNavigationMixin } from '@/utils/mixins/prelogin-navigation';
import { AnalyticsMixin } from '@/utils/mixins/analytics';
import ClyFormField from '@/components/countly/lib-components/form/cly-form-field.vue';
import ClyForm from '@/components/countly/lib-components/form/cly-form.vue';
import ClyFormStep from '@/components/countly/lib-components/form/cly-form-step.vue';
import MycBigButton from '@/components/common/myc-big-button.vue';
import PasswordValidation from './containers/password-validation.vue';

export default {
  name: 'CreateAccountView',
  components: {
    ClyForm,
    ClyFormStep,
    ClyFormField,
    PasswordValidation,
    MycBigButton,
  },
  mixins: [PreloginNavigationMixin, AnalyticsMixin],
  data() {
    return {
      termsAndConditionsChecked: false,
      privacyPolicyChecked: false,
      passwordFocused: false,
      signup: {
        fullName: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
      },
    };
  },
  computed: {
    areCheckboxesOK() {
      return this.termsAndConditionsChecked && this.privacyPolicyChecked;
    },
  },
  watch: {
    'signup.fullName': function (newValue) {
      this.signup.firstName = newValue.split(' ')[0] || '';
      this.signup.lastName = newValue.split(' ').slice(1).join(' ') || '';
    },
  },
  created() {
    this.tryResuming(this.$router);
  },
  mounted() {
    this.persistUtmTags();

    if (this.$route.query.cly_id) {
      this.setDeviceId('landing', this.$route.query.cly_id);
    }

    this.startTimedEvent('signup');
    this.trackEvent('signupBegin');
  },
  methods: {
    ...mapActions(useAuthStore, ['submitSignUp']),
    persistUtmTags() {
      const utmTags = [];

      if (this.$route.query.utm_source) {
        utmTags.push({ key: 'utm_source', value: this.$route.query.utm_source });
      }

      if (this.$route.query.utm_medium) {
        utmTags.push({ key: 'utm_medium', value: this.$route.query.utm_medium });
      }

      if (this.$route.query.utm_campaign) {
        utmTags.push({ key: 'utm_campaign', value: this.$route.query.utm_campaign });
      }

      if (utmTags.length) {
        localStorage.setItem('utm_tags', JSON.stringify(utmTags));
      }
    },
    getSignupSubmission() {
      const { fullName, ...payload } = this.signup;
      return this.submitSignUp(payload);
    },
    onSuccess() {
      this.sendAnalyticsEvents();
      this.$router.push({
        name: 'Loading',
        query: {
          ...this.$route.query,
          referrer: 'verification',
        },
      });
    },
    sendAnalyticsEvents() {
      this.eventSucceed('signupUser', {
        FirstName: this.signup.firstName, Name: this.signup.lastName, Email: this.signup.email, From: 'Signup',
      });
      this.setUserProfile('user', {
        name: this.signup.firstName,
        email: this.signup.email,
      });
      this.setUserProfile('landing', {
        name: this.signup.firstName,
        email: this.signup.email,
      });
    },
    onError(err, response) {
      let message;
      if (typeof response.error !== 'undefined' && typeof response.error.payload !== 'undefined' && typeof response.error.payload.errors !== 'undefined') {
        const invalidFields = response.error;
        message = `${invalidFields}`;
        message = message.startsWith('Error:') ? message.substring(7) : message;
      } else {
        message = this.$t(err.message) || this.$t('mycountly.errors.something-went-wrong');
      }
      this.eventFailed('signupUser', message);
      this.submissionAllowed = true;
      this.$clyNotify({ message, sticky: false, type: 'error' });
    },
    handleEnter(request, v) {
      if (request.pending || !v.isSubmissionAllowed || !this.areCheckboxesOK) return;
      request.submit(this.getSignupSubmission);
    },
  },
};
</script>
